import { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { ReactTagManager } from "react-gtm-ts";
import { useNavigate, Link } from "react-router-dom";
import { useOnboarding } from "@/hooks/useOnboarding";
import { getFlowConfig } from "@/data/flows";
import BrandLogo from "@/components/BrandLogo";
import Container from "@/components/Container";
import Page from "@/components/Page";
import Button from "@/components/Button";
import Chevron from "@/assets/icons/chevron.svg?react";
import Mixpanel from "@/services/tracking";
import { getLabellingInfo } from "@/api";

type Props = {
  flowId: string;
};

export default function GenericLandingPage({ flowId }: Props) {
  const { resetAnswers, setCurrentFlow } = useOnboarding();
  const navigate = useNavigate();
  const flowConfig = getFlowConfig(flowId);
  const landing = flowConfig?.landing;
  const [hasLabel, setHasLabel] = useState<boolean>(false);

  if (!flowConfig || !landing) {
    return null;
  }

  useEffect(() => {
    getLabellingInfo()
      .then((e) => {
        setHasLabel(e.ok);
      })
      .catch(() => setHasLabel(false));
  }, []);

  const onStart = () => {
    resetAnswers();
    setCurrentFlow(flowId);
    Mixpanel.register({ flow: flowId });
    Mixpanel.track("OnboardingStarted");
    const entryStep = flowConfig.steps[flowConfig.entryStepId];
    navigate(`/steg/${entryStep.slug}`);
    ReactTagManager.action({
      event: "web_onboarding_begin_questionnaire",
    });
  };

  return (
    <>
      <Helmet
        title={flowConfig.pageTitle}
        style={[
          {
            cssText: `
              body {
                background: var(--questionnaire-bg);
              }
            `,
          },
        ]}
      />
      <Container>
        <BrandLogo />

        <Page justifyContent="flex-end">
          <h1>{landing.headline}</h1>

          <ol className="checkmarks">
            {landing.bulletPoints.map((point, i) => (
              <li key={i}>{point}</li>
            ))}
          </ol>

          {landing.additionalText && <p>{landing.additionalText}</p>}

          <div className="text-right">
            <Button onClick={onStart} iconRight={<Chevron />}>
              {landing.ctaText}
            </Button>
          </div>

          <small
            className="disclaimer mt-20"
            dangerouslySetInnerHTML={{ __html: landing.disclaimer }}
          />

          {hasLabel && (
            <small className="disclaimer mt-20">
              Läs mer om <Link to="/bms">Friska Platform</Link>.
            </small>
          )}
        </Page>
      </Container>
    </>
  );
}
